<svelte:options
	customElement={{
		tag: 'rf-light-table',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module>
	/**
	 * Types
	 */
	export interface ReframeLightTableProps {
		id?: string;
		tag?: string;
		debug?: boolean;
		responsive?: boolean;
		'static-positioning'?: boolean;
		'in-inspector'?: boolean;
		'no-collection'?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Import
	 */
	import { v4 as uuidv4 } from 'uuid';
	import classNames from 'classnames';
	import { get } from 'svelte/store';
	import { onDestroy, onMount } from 'svelte';
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import type { RFCanvas } from '@src/types/reframe';
	import cssVariables from '@src/utils/cssvariables';
	import debounced from '@src/utils/debounced';
	import RfLightTableGui from './rf-light-table-gui.svelte';
	import parseBool from '@src/utils/bool';
	import { useCollection } from '@src/composables/useCollection.svelte.ts';
	import { emitEvent } from '@src/utils/events';
	import { BREAKPOINTS } from '@src/utils/breakpoints';
	import { createEventDispatcher } from 'svelte';

	/**
	 * Props
	 */
	let {
		tag = 'section',
		id = '',
		debug = false,
		'static-positioning': staticPositioning = false,
		'in-inspector': inInspector = false,
		responsive: responsive = true,
		'no-collection': noCollection = false,
	}: ReframeLightTableProps = $props();

	/**
	 * Constants
	 */
	const name = 'rf-light-table';
	const componentId = uuidv4();
	const dispatch = createEventDispatcher();
	/**
	 * Composables
	 */
	const { element, forEachSlottedElement, getSlottedElements } = useSlottedElements();
	export const collection = parseBool(noCollection)
		? undefined
		: useCollection(id, {
				element,
				moduleName: name,
				componentId,
			});

	/**
	 * States
	 */
	let selectedElementId: string | null = $state(null);
	let selectedElementData: {
		x: number;
		y: number;
		width: number;
		height: number;
		rotation: number;
		opacity: number;
		order: number;
		interactive: boolean;
	} | null = $state(null);
	let totalElements = $state(0);
	let orderArray = $state<{ domIndex: number; order: number }[]>([]);
	let baseHeight = $state(0);
	let baseWidth = $state(0);
	let farthestElementOverflow = $state(0);
	let htmlCopied = $state(false);
	let staticFinalPositions = $state(false);

	/**
	 * Variables
	 */
	let isDragging = false;
	let activeElementId: string | null = null;
	let startX = 0;
	let startY = 0;
	let startElementX = 0;
	let startElementY = 0;
	let containerObserver: ResizeObserver | null = null;

	/**
	 * Methods
	 */
	const getNumAttribute = (attr: string, fallback: number, el: HTMLElement) => parseFloat(el.getAttribute(attr) ?? `${fallback}`);

	const calculateFarthestElementOverflow = (): void => {
		const containerElement = get(element);
		if (!containerElement) return;

		let maxBottomY = 0;

		// Calculate the farthest element from the bottom of the container
		forEachSlottedElement((element) => {
			if (element instanceof HTMLElement && element.tagName === 'RF-LIGHT-TABLE-ELEMENT') {
				const y = getNumAttribute('y', 0, element);
				const height = element.getBoundingClientRect().height;

				const elementCenterY = ((y + 1) * baseHeight) / 2;
				const elementBottomY = elementCenterY + height / 2;

				maxBottomY = Math.max(maxBottomY, elementBottomY + 4);
			}
		});

		const overflow = Math.max(0, maxBottomY - baseHeight);
		farthestElementOverflow = overflow;
	};

	const debouncedCalculateFarthestElementOverflow = debounced(calculateFarthestElementOverflow, 100);

	const updateSelectedElementData = (element: HTMLElement) => {
		// Update the selected element data
		if (!element) return;

		const canvasElement = element.querySelector('rf-canvas') as RFCanvas;

		selectedElementData = {
			x: getNumAttribute('x', 0, element),
			y: getNumAttribute('y', 0, element),
			width: getNumAttribute('width', 0, element),
			height: getNumAttribute('height', 0, element),
			rotation: canvasElement?.isInteractive ? 0 : getNumAttribute('rotation', 0, element),
			order: getNumAttribute('order', 0, element),
			opacity: getNumAttribute('opacity', 1, element),
			interactive: canvasElement?.isInteractive,
		};

		// Recalculate the farthest element from the bottom of the container
		calculateFarthestElementOverflow();
	};

	const selectElement = (element: HTMLElement) => {
		// Remove active class from all other elements
		forEachSlottedElement((otherElement) => {
			if (otherElement instanceof HTMLElement && otherElement.tagName === 'RF-LIGHT-TABLE-ELEMENT' && otherElement !== element) {
				otherElement.classList.remove('rf-light-table-element--active');
			}
		});

		// Store the selected element ID
		selectedElementId = element.getAttribute('data-id') ?? null;

		// Add active class to the selected element
		if (parseBool(debug)) {
			element.classList.add('rf-light-table-element--active');
		}

		// Update the selected element data
		updateSelectedElementData(element);
	};

	const handleMouseDown = (event: CustomEvent<{ clientX: number; clientY: number }>, wrapper: HTMLElement) => {
		if (!wrapper) return;
		// Select the element if it's not already selected
		if (selectedElementId !== wrapper.getAttribute('data-id')) {
			selectElement(wrapper);
		}

		isDragging = true;
		activeElementId = wrapper.getAttribute('data-id') ?? null;
		wrapper.classList.add('rf-light-table-element--dragging');

		const { clientX, clientY } = event.detail;

		startX = clientX ?? 0;
		startY = clientY ?? 0;

		const currentX = getNumAttribute('x', 0, wrapper);
		const currentY = getNumAttribute('y', 0, wrapper);
		startElementX = currentX;
		startElementY = currentY;

		event.preventDefault();
	};

	const handleMouseMove = (event: MouseEvent) => {
		const currentElement = get(element);
		if (currentElement && currentElement?.clientWidth < BREAKPOINTS.s && parseBool(responsive)) return;
		if (!isDragging || !activeElementId || parseBool(staticPositioning) || !currentElement) return;

		const deltaX = event.clientX - startX;
		const deltaY = event.clientY - startY;

		const normalizedDeltaX = (deltaX / baseWidth) * 2;
		const normalizedDeltaY = (deltaY / baseHeight) * 2;

		const newX = (startElementX + normalizedDeltaX).toFixed(2);
		const newY = (startElementY + normalizedDeltaY).toFixed(2);

		// Find the active element in the event path
		const activeElement = getSlottedElements().find(
			(el): el is HTMLElement =>
				el instanceof HTMLElement && el.tagName === 'RF-LIGHT-TABLE-ELEMENT' && el.getAttribute('data-id') === activeElementId
		);

		if (!activeElement) return;

		activeElement.setAttribute('x', newX.toString());
		activeElement.setAttribute('y', newY.toString());

		activeElement.style.setProperty('--rf-element-x', newX.toString());
		activeElement.style.setProperty('--rf-element-y', newY.toString());

		// Update the debug GUI during dragging
		updateSelectedElementData(activeElement);
	};

	const handleMouseUp = () => {
		const currentElement = get(element);
		if (!activeElementId || parseBool(staticPositioning)) return;
		if (currentElement && currentElement?.clientWidth < BREAKPOINTS.s && parseBool(responsive)) return;
		// Find the active element in the event path
		const activeElement = getSlottedElements().find(
			(el): el is HTMLElement =>
				el instanceof HTMLElement && el.tagName === 'RF-LIGHT-TABLE-ELEMENT' && el.getAttribute('data-id') === activeElementId
		);
		if (!activeElement) return;

		activeElement.classList.remove('rf-light-table-element--dragging');
		isDragging = false;
		activeElementId = null;
	};

	const handleDocumentClick = (event: MouseEvent) => {
		const currentElement = get(element);
		if (!selectedElementId || parseBool(staticPositioning)) return;
		if (currentElement && currentElement?.clientWidth < BREAKPOINTS.s && parseBool(responsive)) return;
		// Find the selected element in the document
		const selectedElement = getSlottedElements().find(
			(el): el is HTMLElement =>
				el instanceof HTMLElement && el.tagName === 'RF-LIGHT-TABLE-ELEMENT' && el.getAttribute('data-id') === selectedElementId
		);
		if (!selectedElement) return;

		const path = event.composedPath();
		const isElementClick = path.some((el) => el instanceof HTMLElement && el.tagName === 'RF-LIGHT-TABLE-ELEMENT');
		const isGUIPanelClick = path.some((el) => el instanceof HTMLElement && el.classList?.contains('rf-light-table-element-gui'));

		if (!isElementClick && !isGUIPanelClick) {
			selectedElement.classList.remove('rf-light-table-element--active');
			selectedElementId = null;
			selectedElementData = null;
		}
	};

	/**
	 * Elements initialization functions
	 */
	const initOrder = () => {
		// Get all elements and their `order` attribute (as float or null)
		const elements = getSlottedElements().filter(
			(el): el is HTMLElement => el instanceof HTMLElement && el.tagName === 'RF-LIGHT-TABLE-ELEMENT'
		);

		const orders = elements.map((el) => getNumAttribute('order', 0, el) || null);

		// Separate elements into those with and without a valid order
		const ordered = orders.map((order, i) => (order !== null ? { domIndex: i, order: Math.max(order, 1) } : null)).filter(Boolean) as {
			domIndex: number;
			order: number;
		}[];

		const unordered = orders.map((order, i) => (order === null ? { domIndex: i, order: NaN } : null)).filter(Boolean) as {
			domIndex: number;
			order: number;
		}[];

		// Sort the ordered elements by ascending `order` value
		const sortedOrdered = [...ordered].sort((a, b) => a.order - b.order);

		// Distribute elements into final array based on virtual order slots
		const total = orders.length;
		const finalArray: { domIndex: number; order: number }[] = [];

		for (let virtualOrder = 1; finalArray.length < total; virtualOrder++) {
			// All elements with the current virtual order (could be multiple due to same value)
			const sameOrder = sortedOrdered.filter((el) => el.order === virtualOrder);

			if (sameOrder.length > 0) {
				finalArray.push(...sameOrder);
				// Remove added elements from the sorted list
				sameOrder.forEach((el) => {
					const index = sortedOrdered.indexOf(el);
					if (index > -1) sortedOrdered.splice(index, 1);
				});
			} else if (unordered.length) {
				finalArray.push(unordered.shift()!);
			} else if (sortedOrdered.length) {
				finalArray.push(sortedOrdered.shift()!);
			}
		}
		// Normalize the order indexes
		orderArray = finalArray.map((el, i) => ({ ...el, order: i }));
	};

	const initSlottedElement = (el: HTMLElement) => {
		const currentElement = get(element);
		if (!currentElement) return;

		let x = getNumAttribute('x', 0, el),
			y = getNumAttribute('y', 0, el),
			opacity = getNumAttribute('opacity', 1, el);
		let rotation = getNumAttribute('rotation', 0, el),
			width = getNumAttribute('width', 0, el),
			height = getNumAttribute('height', 0, el);

		initOrder();

		const setSize = () => {
			el.setAttribute('width', `${width}`);
			el.setAttribute('height', `${height}`);
			el.style.setProperty('--rf-element-width', `${width}`);
			el.style.setProperty('--rf-element-height', `${height}`);
		};

		const setRotation = () => {
			el.setAttribute('rotation', `${rotation}`);
			el.style.setProperty('--rf-element-rotation', `${rotation}`);
		};

		const canvas = el.querySelector('rf-canvas') as RFCanvas | null;

		if (canvas) {
			const onCanvasLoad = (e: CustomEvent<{ naturalWidth: number; naturalHeight: number }>) => {
				const { naturalWidth, naturalHeight } = e.detail;
				// If the width or height is not set, set it to the natural width or height of the canvas
				// and normalize it by baseWidth
				if (!width) width = +(Math.min(naturalWidth, currentElement.offsetWidth / 2) / baseWidth).toFixed(4);
				if (!height) height = +(Math.min(naturalHeight, currentElement.offsetHeight / 2) / baseWidth).toFixed(4);
				setSize();
				canvas.removeEventListener('load', onCanvasLoad as EventListener);
			};

			canvas.addEventListener('load', onCanvasLoad as EventListener);

			// If the canvas is interactive, remove any rotation styles
			canvas.isInteractive ? (el.removeAttribute('rotation'), el.style.removeProperty('--rf-element-rotation')) : setRotation();
		} else {
			if (!width) width = +(el.offsetWidth / baseWidth).toFixed(4);
			if (!height) height = +(el.offsetHeight / baseWidth).toFixed(4);
			setSize();
			setRotation();
		}

		// Base attributes and styles
		el.setAttribute('data-id', uuidv4());
		if (parseBool(debug)) el.setAttribute('debug', 'true');
		if (parseBool(staticPositioning)) el.setAttribute('static-positioning', 'true');
		Object.entries({ x, y, opacity }).forEach(([k, v]) => {
			el.setAttribute(k, `${v}`);
			el.style.setProperty(`--rf-element-${k}`, `${v}`);
		});

		if (el.getAttribute('data-id') === selectedElementId) {
			updateSelectedElementData(el);
		}
	};

	/**
	 * DEBUG ONLY: Property change handlers
	 */
	const debouncedRefreshCanvas = debounced(async (canvasElement: HTMLElement & { refresh: () => void }) => {
		await canvasElement.refresh();
	}, 100);

	const handleElementResize = (event: CustomEvent<{ width: number; height: number }>, element: HTMLElement) => {
		element.setAttribute('width', ((event.detail.width || 1) / baseWidth).toFixed(4).toString());
		element.setAttribute('height', ((event.detail.height || 1) / baseWidth).toFixed(4).toString());
		element.style.setProperty('--rf-element-width', `${((event.detail.width || 1) / baseWidth).toFixed(4)}`);
		element.style.setProperty('--rf-element-height', `${((event.detail.height || 1) / baseWidth).toFixed(4)}`);
		const canvasElement = element.querySelector('rf-canvas');
		if (canvasElement) {
			debouncedRefreshCanvas(canvasElement as HTMLElement & { refresh: () => void });
		}
		updateSelectedElementData(element);
	};

	const handlePropertyChange = (property: string, event: CustomEvent<{ [key: string]: number }>) => {
		if (!selectedElementId) return;

		// Find the element using getSlottedElements
		const selectedElement = getSlottedElements().find(
			(el): el is HTMLElement =>
				el instanceof HTMLElement && el.tagName === 'RF-LIGHT-TABLE-ELEMENT' && el.getAttribute('data-id') === selectedElementId
		);

		if (!selectedElement) return;

		// Update the selected element's property
		selectedElement.setAttribute(property, event.detail.toString());
		selectedElement.style.setProperty(`--rf-element-${property}`, `${event.detail}`);

		// Update the selected element data
		updateSelectedElementData(selectedElement);
	};

	const handleOrderChange = (value: number) => {
		if (!selectedElementId) return;

		// Get the index of the changed element
		let newOrderArray = [...orderArray];
		const changedElementIndex = getSlottedElements().findIndex((el) => el.getAttribute('data-id') === selectedElementId);
		const newOrderElement = newOrderArray.find((v) => v.domIndex === changedElementIndex);
		const newOrderElementIndex = newOrderArray.findIndex((v) => v.domIndex === changedElementIndex);

		// Remove the changed element from the array
		newOrderArray.splice(newOrderElementIndex, 1);

		// If the new order is the total number of elements, add the changed element to the end of the array
		if (value === totalElements) {
			newOrderArray.push(newOrderElement as { domIndex: number; order: number });
		} else {
			// Otherwise, add the changed element to the beginning of the array
			newOrderArray.unshift(newOrderElement as { domIndex: number; order: number });
		}

		orderArray = newOrderArray;
	};

	const copyAllHTML = () => {
		// Get all elements and remove the data-id, style, debug and class attributes
		const html = [...Array.from(getSlottedElements())]
			.map((el) => {
				if (!(el instanceof HTMLElement)) return '';

				const clone = el.cloneNode(true) as HTMLElement;
				clone.removeAttribute('data-id');
				clone.removeAttribute('style');
				clone.removeAttribute('debug');
				clone.removeAttribute('class');
				clone.removeAttribute('static-positioning');

				return clone.outerHTML;
			})
			.join('');

		// Copy the HTML to the clipboard
		navigator.clipboard.writeText(
			`<rf-light-table ${parseBool(staticFinalPositions) ? 'static-positioning="true"' : ''}>${html}</rf-light-table>`
		);

		// Set the htmlCopied state to true
		htmlCopied = true;
		setTimeout(() => {
			htmlCopied = false;
		}, 1000);
	};

	/**
	 * Effects
	 */
	$effect(() => {
		// Set the order of the elements when the orderArray changes
		orderArray.forEach((v, i) => {
			const element = getSlottedElements().find((_, j) => v?.domIndex === j) as HTMLElement;
			if (element) {
				element.setAttribute('order', (i + 1).toString());
				element.style.setProperty('--rf-element-order', `${i + 1}`);
			}
		});
	});

	/**
	 * Lifecycle
	 */
	onMount(() => {
		const currentElement = get(element);
		if (!currentElement) return;

		// Set initial values for baseWidth and baseHeight
		baseHeight = window.innerHeight;
		baseWidth = currentElement.clientWidth ?? window.innerWidth;

		containerObserver = new ResizeObserver(() => {
			baseHeight = window.innerHeight;
			baseWidth = currentElement.clientWidth ?? window.innerWidth;
			debouncedCalculateFarthestElementOverflow();
			emitEvent('rf-page-resize');
		});
		containerObserver.observe(currentElement);

		currentElement.addEventListener('slotchange', () => {
			selectedElementId = null;
			selectedElementData = null;
			forEachSlottedElement((element) => {
				if (element instanceof HTMLElement && element.tagName === 'RF-LIGHT-TABLE-ELEMENT') {
					// If static positioning is not enabled, add an event listener to the grabber
					if (!parseBool(staticPositioning)) {
						element.addEventListener('grabber-mousedown', (event) => {
							if (currentElement.clientWidth < BREAKPOINTS.s && parseBool(responsive)) return;
							handleMouseDown(event as CustomEvent<{ clientX: number; clientY: number }>, element);
						});
					} else {
						element.classList.add('rf-light-table-element--static');
					}
					if (parseBool(responsive)) {
						element.classList.add('rf-light-table-element--responsive');
					}
					// Add an event listener to the element resize
					element.addEventListener('element-resize', (event) => {
						if (currentElement.clientWidth < BREAKPOINTS.s && parseBool(responsive)) return;
						handleElementResize(event as CustomEvent<{ width: number; height: number }>, element);
					});
					initSlottedElement(element);
				}
			});
			totalElements = getSlottedElements().length;
		});
	});

	onDestroy(() => {
		const currentElement = get(element);
		if (!currentElement) return;
		if (containerObserver) containerObserver.disconnect();

		forEachSlottedElement((element) => {
			if (element instanceof HTMLElement && element.tagName === 'RF-LIGHT-TABLE-ELEMENT') {
				if (!parseBool(staticPositioning)) {
					element.removeEventListener('grabber-mousedown', (event) =>
						handleMouseDown(event as CustomEvent<{ clientX: number; clientY: number }>, element)
					);
				}
				element.removeEventListener('element-resize', (event) =>
					handleElementResize(event as CustomEvent<{ width: number; height: number }>, element)
				);
			}
		});
	});
</script>

<svelte:document onmousedown={handleDocumentClick} />
<svelte:window onmousemove={handleMouseMove} onmouseup={handleMouseUp} />
<slot name="header"></slot>
{#if parseBool(debug) && !parseBool(inInspector)}
	<div class="rf-light-table__debug-container">
		<div class="rf-light-table__debug-input-container">
			<input
				type="checkbox"
				id="rf-light-table-debug"
				checked={staticFinalPositions}
				oninput={() => (staticFinalPositions = !staticFinalPositions)}
			/>
			<label for="rf-light-table-debug">Static final positions</label>
		</div>
		<button class="rf-light-table__debug-button" onclick={copyAllHTML}>
			{htmlCopied ? 'Copied!' : 'Copy rf-light-table HTML'}
		</button>
	</div>
{/if}
{#if parseBool(debug) && selectedElementId && selectedElementData}
	<RfLightTableGui
		x={selectedElementData.x}
		y={selectedElementData.y}
		width={selectedElementData.width}
		height={selectedElementData.height}
		rotation={selectedElementData.rotation}
		order={selectedElementData.order}
		opacity={selectedElementData.opacity}
		interactive={selectedElementData.interactive}
		on:rotation-change={(v) => handlePropertyChange('rotation', v)}
		on:opacity-change={(v) => handlePropertyChange('opacity', v)}
		on:order-change={(v) => handleOrderChange(parseFloat(v.detail))}
		inInspector={parseBool(inInspector)}
		{selectedElementId}
		{totalElements}
	/>
{/if}
<svelte:element
	this={tag}
	class={classNames('rf-light-table', {
		'rf-light-table--static-positioning': parseBool(staticPositioning),
		'rf-light-table--responsive': parseBool(responsive),
	})}
	bind:this={$element}
	part="rf-light-table"
	style={cssVariables({ '--rf-light-table-overflow': `${farthestElementOverflow}px`, '--rf-light-table-width': `${baseWidth}px` })}
>
	<slot></slot>
</svelte:element>
<slot name="footer"></slot>

<style lang="scss" global>
	@use 'assets/scss/core/rf-breakpoints' as breakpoint;

	.rf-light-table {
		box-sizing: border-box;
		display: block;
		position: relative;
		overflow: hidden;
		width: 100%;
		height: calc(100svh + var(--rf-light-table-overflow, 0px));

		::slotted(rf-light-table-element) {
			position: absolute;
			transform-origin: center center;
			user-select: none;
			transform: translate(
					calc((var(--rf-element-x, 0) + 1) * calc(var(--rf-light-table-width, 0px) / 2) - 50%),
					calc((var(--rf-element-y, 0) + 1) * 50svh - 50%)
				)
				rotate(calc(var(--rf-element-rotation, 0) * 1deg));
			display: block;
			opacity: var(--rf-element-opacity, 1);
			z-index: var(--rf-element-order, 0);
			width: calc(var(--rf-element-width, 200px) * var(--rf-light-table-width, 0px));
			height: calc(var(--rf-element-height, 200px) * var(--rf-light-table-width, 0px));
		}

		&.rf-light-table--responsive {
			@container rf-light-table (width < #{breakpoint.$s}) {
				height: auto;
				display: flex;
				flex-direction: column;
				padding: 0 var(--rf-spacing-s, 8px);
				gap: var(--rf-spacing-s, 8px);
			}
			::slotted(rf-light-table-element) {
				@container rf-light-table (width < #{breakpoint.$s}) {
					position: relative;
					width: 100%;
					height: auto;
					opacity: 1;
					transform: unset;
				}
			}
		}

		::slotted(rf-light-table-element:hover),
		::slotted(rf-light-table-element.rf-light-table-element--active),
		::slotted(rf-light-table-element.rf-light-table-element--dragging) {
			outline: 2px solid var(--rf-info-color, #4dbee0);
			@container rf-light-table (width < #{breakpoint.$s}) {
				outline: none;
			}
		}

		::slotted(rf-light-table-element.rf-light-table-element--static) {
			user-select: initial;
		}

		&.rf-light-table--static-positioning {
			::slotted(rf-light-table-element:hover) {
				outline: none;
			}
		}

		&__debug-container {
			position: fixed;
			top: var(--rf-spacing-s, 8px);
			left: var(--rf-spacing-s, 8px);
			display: flex;
			flex-direction: column;
			z-index: 10;
			gap: var(--rf-spacing-s, 8px);
		}
		&__debug-button {
			padding: var(--rf-spacing-s, 8px);
			background-color: rgba(0, 0, 0, 0.7);
			border: none;
			color: white;
			cursor: pointer;
			border-radius: 4px;
			&:hover {
				background-color: rgba(0, 0, 0, 0.9);
			}
		}
	}
</style>
